// use-teams.ts — Teams suite, query.
//
// Reads the merged teams list from TeamsProvider's context. The provider owns
// the public + owned subscriptions; this hook just projects them to the
// standard QueryResult envelope.
//
// Because the list is the MERGE of two queries, so is the status: the envelope
// only reads Success once both collections in it have settled. A surface that
// needs one collection alone reads `publicTeams` / `ownedTeams` off the context
// with their own statuses instead of paying for the other's round trip — the
// landing hero is the case in point.

'use client';

import type { TeamPreviewListItem } from '@cabal/shared/domain/session/team/types.js';
import { QUERY_STATUS, type QueryResult, type QueryStatus } from '@/types/async-protocol';
import { useTeamsContext } from './TeamsProvider';

export function useTeams(): QueryResult<TeamPreviewListItem[]> {
  const ctx = useTeamsContext();
  const status = mergeStatuses(ctx.status, ctx.ownedStatus);
  return {
    data: status === QUERY_STATUS.Success ? ctx.teams : null,
    status,
    error: ctx.error,
    refetch: ctx.refetch,
  };
}

function mergeStatuses(publicStatus: QueryStatus, ownedStatus: QueryStatus): QueryStatus {
  if (publicStatus === QUERY_STATUS.Loading || ownedStatus === QUERY_STATUS.Loading) {
    return QUERY_STATUS.Loading;
  }
  if (publicStatus === QUERY_STATUS.Errored || ownedStatus === QUERY_STATUS.Errored) {
    return QUERY_STATUS.Errored;
  }
  return QUERY_STATUS.Success;
}
